import { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/th";
import DateRangePicker from "../../common/DateRangePicker";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputAdornment from "@mui/material/InputAdornment";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TablePagination from "@mui/material/TablePagination";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SearchIcon from "@mui/icons-material/Search";
import { exportBdcCreditNotes, getBdcCreditNotePdf, listBdcCreditNotes } from "../../../services/bdcCreditNote";
import { BdcEdiPreview, createBdcEdiExport, downloadBdcEdiExport, previewBdcEdiExport } from "../../../services/bdcEdi";
import { featureAccess } from "../../../permissions/access";
import { BdcCreditNoteListParams, BdcCreditNoteStatus, BdcCreditNoteSummary } from "../../../types/bdc.type";
import { saveBlobResponse } from "../../../utils/downloadBlob";
import { useDebounce } from "../../../utils/hooks/useDebounce";
import { useUserRole } from "../../../utils/hooks/useUserRole";
import { BdcApiError, readBdcError } from "../apiError";
import BranchChip from "../Branch/BranchChip";
import BranchSelector from "../Branch/BranchSelector";
import { useBdcBranchSelection, useBdcBranches } from "../Branch/useBdcBranches";
import { BDC_CN_EDI_MAX, BDC_EDI_PATH, bdcCreditNoteLink, bdcNewCreditNoteLink } from "../constants";
import { formatBytes } from "../Edi/edi";
import { baht, pcs, thaiDate, todayBkk } from "../format";
import { monthStart } from "../Invoice/Register";
import { openPdfResponse } from "../Invoice/pdf";
import { CARD_SX, SCROLL_SX, TABLE_SX } from "../styles";
import { useBdcDcs } from "../useBdcDcs";
import { useBlobExport } from "../useBlobExport";
import { canExportRetsev, CN_STATUS, retsevProblem } from "./cn";
import CreditNoteStatusChip from "./CreditNoteStatusChip";

const ALL = "all";
const PAGE_SIZES = [50, 100];

/** The list query: blank filters are left out; the range is in CN dates. */
export const creditNoteParams = (filters: {
  dateFrom: string;
  dateTo: string;
  status: string;
  dcCode: string;
  search: string;
  isTest: boolean;
  companyId?: string;
}): Omit<BdcCreditNoteListParams, "page" | "limit"> => ({
  date_from: filters.dateFrom || undefined,
  date_to: filters.dateTo || undefined,
  ...(filters.status !== ALL ? { status: filters.status as BdcCreditNoteStatus } : {}),
  ...(filters.dcCode !== ALL ? { dc_code: filters.dcCode } : {}),
  ...(filters.search.trim() ? { search: filters.search.trim() } : {}),
  ...(filters.isTest ? { is_test: true } : {}),
  ...(filters.companyId ? { company_id: filters.companyId } : {}),
});

/**
 * BDC · ใบลดหนี้ (CN) — the CNs of a range of CN dates by DC / status: open one to edit a draft,
 * issue, cancel or print it; tick issued CNs → the RETSEV file for 7-11 (same flow and history as
 * ไฟล์ส่ง 7-11 — the CNs then lock as ส่ง 7-11 แล้ว); ทะเบียน CN to Excel.
 */
export default function BdcCreditNoteList() {
  const role = useUserRole();
  const canWrite = featureAccess.bdcCreditNoteWrite(role);
  const { branches, ready: branchesReady } = useBdcBranches();
  const branch = useBdcBranchSelection(branches);
  const { dcs } = useBdcDcs();
  const { busy: exportBusy, run } = useBlobExport();

  const today = todayBkk();
  const [dateFrom, setDateFrom] = useState(monthStart(today));
  const [dateTo, setDateTo] = useState(today);
  const [status, setStatus] = useState<string>(ALL);
  const [dcCode, setDcCode] = useState<string>(ALL);
  const [searchText, setSearchText] = useState("");
  const search = useDebounce(searchText, 400);
  const [isTest, setIsTest] = useState(false);
  const [pageState, setPageState] = useState<{ key: string; page: number }>({ key: "", page: 0 });
  const [limit, setLimit] = useState(PAGE_SIZES[0]);

  const [rows, setRows] = useState<BdcCreditNoteSummary[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pdfBusy, setPdfBusy] = useState<string | null>(null);
  // ticked CNs survive paging (kept whole, the RETSEV request needs only their uuids)
  const [picked, setPicked] = useState<Map<string, BdcCreditNoteSummary>>(() => new Map());
  const [preview, setPreview] = useState<BdcEdiPreview | null>(null);
  const [ediBusy, setEdiBusy] = useState<"preview" | "create" | null>(null);
  const [failure, setFailure] = useState<BdcApiError | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const params = useMemo(
    () => creditNoteParams({ dateFrom, dateTo, status, dcCode, search, isTest, companyId: branch.companyId }),
    [dateFrom, dateTo, status, dcCode, search, isTest, branch.companyId],
  );
  const paramsKey = JSON.stringify(params);
  const page = pageState.key === paramsKey ? pageState.page : 0;
  const setPage = (next: number) => setPageState({ key: paramsKey, page: next });

  const load = useCallback(async () => {
    if (!branchesReady) return;
    setLoading(true);
    try {
      const {
        data: { data },
      } = await listBdcCreditNotes({ ...params, page: page + 1, limit });
      setRows(data?.rows ?? []);
      setCount(data?.count ?? 0);
    } catch {
      setRows([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, [params, page, limit, branchesReady]);

  useEffect(() => {
    load();
  }, [load]);

  const pickRange = (from: Dayjs, to: Dayjs) => {
    setDateFrom(from.format("YYYY-MM-DD"));
    setDateTo(to.format("YYYY-MM-DD"));
  };

  const openPdf = async (note: BdcCreditNoteSummary) => {
    setPdfBusy(note.uuid);
    try {
      openPdfResponse(await getBdcCreditNotePdf(note.uuid), `${note.note_no ?? note.uuid}.pdf`);
    } catch {
      // reported by the interceptor
    } finally {
      setPdfBusy(null);
    }
  };

  const toggle = (note: BdcCreditNoteSummary) =>
    setPicked((current) => {
      const next = new Map(current);
      if (next.has(note.uuid)) next.delete(note.uuid);
      else next.set(note.uuid, note);
      return next;
    });
  const selectable = rows.filter(canExportRetsev);
  const pageAllPicked = selectable.length > 0 && selectable.every((r) => picked.has(r.uuid));
  const pageSomePicked = selectable.some((r) => picked.has(r.uuid));
  const togglePage = (on: boolean) =>
    setPicked((current) => {
      const next = new Map(current);
      selectable.forEach((r) => (on ? next.set(r.uuid, r) : next.delete(r.uuid)));
      return next;
    });

  const pickedNotes = Array.from(picked.values());
  const problem = retsevProblem(pickedNotes, BDC_CN_EDI_MAX);
  const ediRequest = { type: "cn" as const, credit_note_uuids: pickedNotes.map((n) => n.uuid) };

  const runPreview = async () => {
    if (problem) return;
    setEdiBusy("preview");
    setFailure(null);
    setDone(null);
    try {
      const {
        data: { data },
      } = await previewBdcEdiExport(ediRequest);
      setPreview(data);
    } catch (e) {
      setFailure(readBdcError(e, "ตรวจไฟล์ RETSEV ไม่สำเร็จ"));
    } finally {
      setEdiBusy(null);
    }
  };

  const create = async () => {
    setEdiBusy("create");
    setFailure(null);
    try {
      const {
        data: { data },
      } = await createBdcEdiExport(ediRequest);
      setPreview(null);
      setPicked(new Map());
      for (const file of data?.rows ?? []) {
        try {
          // eslint-disable-next-line no-await-in-loop
          saveBlobResponse(await downloadBdcEdiExport(file.uuid), file.file_name);
        } catch {
          // reported by the interceptor; the file stays in ไฟล์ส่ง 7-11 › ประวัติ
        }
      }
      const queued = data?.queued_note_nos ?? [];
      setDone(`สร้าง ${pcs(data?.count ?? 0)} ไฟล์แล้ว${queued.length ? ` · ส่ง 7-11 แล้ว: ${queued.join(", ")}` : ""}`);
    } catch (e) {
      setPreview(null);
      setFailure(readBdcError(e, "สร้างไฟล์ RETSEV ไม่สำเร็จ"));
    } finally {
      setEdiBusy(null);
      load();
    }
  };

  const live = rows.filter((r) => r.status === "issued" || r.status === "queued_7_11");
  const dcOptions = branch.companyId ? dcs.filter((dc) => dc.company_id === branch.companyId) : dcs;

  return (
    <Box sx={{ padding: "8px 0" }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={2} flexWrap="wrap" mb={2}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            BDC · ใบลดหนี้ (CN)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            สร้าง CN จาก INV ที่ออกแล้วของ DC เดียวกัน → ออก CN (ได้เลข) → เลือกหลายใบสร้างไฟล์ RETSEV ส่ง 7-11 ·{" "}
            {isTest ? "กำลังดูชุดทดสอบ TC… (ไม่อยู่ในไฟล์ Excel)" : "ไม่รวมใบทดสอบ TC…"}
          </Typography>
        </Box>
        <Stack direction="row" gap={1}>
          <Tooltip title={isTest ? "Excel ทะเบียนมีเฉพาะ CN จริง — ปิดสวิตช์ชุดทดสอบก่อน" : ""}>
            <span>
              <Button
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                disabled={isTest}
                loading={exportBusy === "register"}
                onClick={() =>
                  run(
                    "register",
                    () => exportBdcCreditNotes({ ...params, is_test: undefined }),
                    `bdc-credit-note-register-${dateFrom}-${dateTo}.xlsx`,
                  )
                }
              >
                Export ทะเบียน CN
              </Button>
            </span>
          </Tooltip>
          {canWrite && (
            <Button variant="contained" startIcon={<AddIcon />} component={RouterLink} to={bdcNewCreditNoteLink()}>
              สร้าง CN
            </Button>
          )}
        </Stack>
      </Stack>

      <Stack gap={2} sx={TABLE_SX}>
        <Card sx={{ ...CARD_SX, p: 2 }}>
          <Stack direction="row" gap={1.5} flexWrap="wrap" alignItems="center">
            <BranchSelector branches={branches} value={branch.selected} onChange={branch.select} />
            <DateRangePicker dateFrom={dayjs(dateFrom)} dateTo={dayjs(dateTo)} onChange={pickRange} />
            <TextField select size="small" label="สถานะ" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ width: 150 }}>
              <MenuItem value={ALL}>ทั้งหมด</MenuItem>
              {(Object.keys(CN_STATUS) as BdcCreditNoteStatus[]).map((s) => (
                <MenuItem key={s} value={s}>
                  {CN_STATUS[s].label}
                </MenuItem>
              ))}
            </TextField>
            <TextField select size="small" label="DC" value={dcCode} onChange={(e) => setDcCode(e.target.value)} sx={{ minWidth: 180 }}>
              <MenuItem value={ALL}>ทุก DC</MenuItem>
              {dcOptions.map((dc) => (
                <MenuItem key={dc.dc_code} value={dc.dc_code}>
                  {dc.dc_code} {dc.dc_name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              placeholder="เลข CN / เลขคืน / INV / PO"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              sx={{ width: 230 }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <FormControlLabel
              control={<Switch checked={isTest} onChange={(e) => setIsTest(e.target.checked)} />}
              label="ชุดทดสอบ (TC…)"
            />
          </Stack>
        </Card>

        {loading && <LinearProgress />}
        {failure && (
          <Alert severity="error" onClose={() => setFailure(null)} data-testid="bdc-cn-error">
            {failure.message}
          </Alert>
        )}
        {done && (
          <Alert severity="success" onClose={() => setDone(null)}>
            {done} · download ซ้ำได้ที่ <RouterLink to={BDC_EDI_PATH}>ไฟล์ส่ง 7-11 › ประวัติ</RouterLink>
          </Alert>
        )}

        <Card sx={CARD_SX}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={1.5}
            flexWrap="wrap"
            sx={{ px: 2, py: 1.25, borderBottom: "1px solid #E3E5EA" }}
          >
            <Typography className="sub">
              ทั้งหมด {pcs(count)} ใบ · หน้านี้ ออกแล้ว {pcs(live.length)} ใบ รวม ฿{baht(live.reduce((s, r) => s + r.grand_total, 0))}
            </Typography>
            {canWrite && (
              <Stack direction="row" gap={1} alignItems="center">
                <Typography className="sub">{picked.size > 0 && problem ? problem : `เลือก ${pcs(picked.size)} ใบ`}</Typography>
                {picked.size > 0 && (
                  <Button size="small" onClick={() => setPicked(new Map())}>
                    ล้าง
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="warning"
                  size="small"
                  disabled={Boolean(problem)}
                  loading={ediBusy === "preview"}
                  onClick={runPreview}
                >
                  สร้างไฟล์ RETSEV ({pcs(picked.size)})
                </Button>
              </Stack>
            )}
          </Stack>
          <Box sx={SCROLL_SX}>
            <table className="bdc-t" data-testid="bdc-cn-list">
              <thead>
                <tr>
                  {canWrite && (
                    <th>
                      <Checkbox
                        size="small"
                        checked={pageAllPicked}
                        indeterminate={pageSomePicked && !pageAllPicked}
                        disabled={selectable.length === 0}
                        onChange={(e) => togglePage(e.target.checked)}
                        slotProps={{ input: { "aria-label": "เลือกทั้งหน้า" } }}
                      />
                    </th>
                  )}
                  <th>เลข CN</th>
                  <th>วันที่</th>
                  <th>สาขา</th>
                  <th>DC</th>
                  <th>เลขคืน 7-11</th>
                  <th>อ้าง INV</th>
                  <th className="n">มูลค่า</th>
                  <th className="n">VAT</th>
                  <th className="n">รวม</th>
                  <th>สถานะ</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.uuid}>
                    {canWrite && (
                      <td>
                        <Checkbox
                          size="small"
                          disabled={!canExportRetsev(row)}
                          checked={picked.has(row.uuid)}
                          onChange={() => toggle(row)}
                          slotProps={{ input: { "aria-label": `เลือก ${row.note_no ?? row.return_doc_no}` } }}
                        />
                      </td>
                    )}
                    <td className="mono">
                      <RouterLink to={bdcCreditNoteLink(row.uuid)}>
                        <b>{row.note_no ?? "(ร่าง)"}</b>
                      </RouterLink>
                    </td>
                    <td>{thaiDate(row.note_date)}</td>
                    <td>
                      <BranchChip branches={branches} companyId={row.company_id} branchCode={row.branch_code} />
                    </td>
                    <td>
                      <span className="dc">{row.dc_code}</span> <span className="sub">{row.dc_name ?? ""}</span>
                    </td>
                    <td className="mono">{row.return_doc_no}</td>
                    <td className="mono">
                      {(row.invoice_nos ?? []).join(", ") || "—"}
                      {row.po_numbers && row.po_numbers.length > 0 && <div className="sub">PO {row.po_numbers.join(", ")}</div>}
                    </td>
                    <td className="n">{baht(row.subtotal)}</td>
                    <td className="n">{baht(row.vat_amount)}</td>
                    <td className="n">
                      <b>{baht(row.grand_total)}</b>
                    </td>
                    <td>
                      <CreditNoteStatusChip note={row} />
                      {row.status === "cancelled" && row.cancel_reason && <div className="sub">{row.cancel_reason}</div>}
                    </td>
                    <td>
                      <Stack direction="row" gap={1}>
                        <Button size="small" variant="outlined" component={RouterLink} to={bdcCreditNoteLink(row.uuid)}>
                          {row.status === "draft" && canWrite ? "แก้ไข" : "ดู"}
                        </Button>
                        {row.status !== "draft" && (
                          <Button size="small" variant="outlined" loading={pdfBusy === row.uuid} onClick={() => openPdf(row)}>
                            PDF
                          </Button>
                        )}
                      </Stack>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && !loading && (
                  <tr>
                    <td colSpan={12} className="sub" style={{ textAlign: "center", padding: 24 }}>
                      ไม่มี CN ในช่วงนี้
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Box>
          <TablePagination
            component="div"
            count={count}
            page={page}
            rowsPerPage={limit}
            rowsPerPageOptions={PAGE_SIZES}
            onPageChange={(_e, next) => setPage(next)}
            onRowsPerPageChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(0);
            }}
            labelRowsPerPage="ต่อหน้า"
          />
        </Card>
      </Stack>

      <Dialog open={preview !== null} onClose={ediBusy ? undefined : () => setPreview(null)} maxWidth="md" fullWidth>
        <DialogTitle>สร้างไฟล์ RETSEV · {pcs(pickedNotes.length)} CN</DialogTitle>
        <DialogContent sx={TABLE_SX} data-testid="bdc-cn-retsev-preview">
          <table className="bdc-t">
            <thead>
              <tr>
                <th>ไฟล์</th>
                <th>CN ในไฟล์</th>
                <th className="n">บรรทัด</th>
                <th className="n">ขนาด</th>
                <th>EAI profile</th>
              </tr>
            </thead>
            <tbody>
              {preview?.files.map((file) => (
                <tr key={file.file_name}>
                  <td className="mono">{file.file_name}</td>
                  <td>
                    {pcs(file.record_count)} CN
                    {file.note_nos && file.note_nos.length > 0 && <div className="sub">{file.note_nos.join(", ")}</div>}
                  </td>
                  <td className="n">{pcs(file.line_count)}</td>
                  <td className="n">{formatBytes(file.bytes)}</td>
                  <td className="mono">{file.eai_profile}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            <AlertTitle>CN {pcs((preview?.locks_note_nos ?? pickedNotes).length)} ใบจะเป็น "ส่ง 7-11 แล้ว" และล็อก</AlertTitle>
            {(preview?.locks_note_nos ?? pickedNotes.map((n) => n.note_no ?? "")).join(", ")} — ยกเลิก CN ไม่ได้อีก
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreview(null)} disabled={ediBusy === "create"}>
            ยกเลิก
          </Button>
          <Button variant="contained" color="warning" loading={ediBusy === "create"} onClick={create}>
            สร้างไฟล์และล็อก
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
